// The client-side model behind the draggable taptree.
//
// A taproot script tree is a binary tree of spend leaves. Crucially, the
// BIP341 commitment sorts each branch's two child hashes (min,max), so the
// address depends ONLY on the multiset of (leaf, depth) pairs, never on
// left/right sibling pairing. That means a drag can express exactly one thing:
// each leaf's DEPTH (its path's reveal cost). We model the tree purely to make
// dragging feel direct, then commit the result as `@depth(n)` pins on every
// spend, which the compiler reproduces exactly, because a real binary tree is
// always Kraft-valid (sum 2^-d == 1).

import type { TapNode } from "../types";

/** A binary tree carrying only leaf indices (names/depths are derived). */
export type ModelTree =
  | { kind: "leaf"; index: number }
  | { kind: "branch"; left: ModelTree; right: ModelTree };

/** Drop the names/depths from the compiler tree; keep the structure. */
export function modelFromTap(node: TapNode): ModelTree {
  return node.kind === "leaf"
    ? { kind: "leaf", index: node.index }
    : { kind: "branch", left: modelFromTap(node.left), right: modelFromTap(node.right) };
}

/** Re-attach names so the model can be laid out like a compiler tree. */
export function tapFromModel(t: ModelTree, nameOf: (index: number) => string): TapNode {
  return t.kind === "leaf"
    ? { kind: "leaf", index: t.index, name: nameOf(t.index), depth: 0 }
    : { kind: "branch", left: tapFromModel(t.left, nameOf), right: tapFromModel(t.right, nameOf) };
}

/** Every leaf index in a subtree (sorted for stable identity). */
export function leavesOf(t: ModelTree): number[] {
  const out: number[] = [];
  (function walk(n: ModelTree) {
    if (n.kind === "leaf") out.push(n.index);
    else {
      walk(n.left);
      walk(n.right);
    }
  })(t);
  return out.sort((a, b) => a - b);
}

/** index -> depth for every leaf. */
export function depthsOf(t: ModelTree): Map<number, number> {
  const out = new Map<number, number>();
  (function walk(n: ModelTree, d: number) {
    if (n.kind === "leaf") out.set(n.index, d);
    else {
      walk(n.left, d + 1);
      walk(n.right, d + 1);
    }
  })(t, 0);
  return out;
}

/** node id (matching the layout walk: "r","rL","rR",...) -> its subtree leaf set. */
export function leafSetsById(t: ModelTree): Map<string, number[]> {
  const out = new Map<string, number[]>();
  (function walk(n: ModelTree, id: string): number[] {
    if (n.kind === "leaf") {
      out.set(id, [n.index]);
      return [n.index];
    }
    const set = [...walk(n.left, `${id}L`), ...walk(n.right, `${id}R`)];
    out.set(id, set);
    return set;
  })(t, "r");
  return out;
}

/** The leaf set of a subtree, joined sorted: a stable identity for a node. */
function setKey(t: ModelTree): string {
  return leavesOf(t).join(",");
}

/**
 * Detach the subtree whose leaf set equals `key`, promoting its sibling into
 * its place. Returns the detached node plus the remaining tree, or null if the
 * key is not found (or is the whole tree, which has no sibling to promote).
 */
function detach(t: ModelTree, key: string): { node: ModelTree; rest: ModelTree } | null {
  if (t.kind === "leaf") return null;
  if (setKey(t.left) === key) return { node: t.left, rest: t.right };
  if (setKey(t.right) === key) return { node: t.right, rest: t.left };
  const l = detach(t.left, key);
  if (l) return { node: l.node, rest: { kind: "branch", left: l.rest, right: t.right } };
  const r = detach(t.right, key);
  if (r) return { node: r.node, rest: { kind: "branch", left: t.left, right: r.rest } };
  return null;
}

/** Wrap the subtree whose leaf set equals `want` in a new branch {subtree, node}. */
function attachSibling(t: ModelTree, want: Set<number>, node: ModelTree): ModelTree | null {
  const set = leavesOf(t);
  if (set.length === want.size && set.every((i) => want.has(i))) {
    return { kind: "branch", left: t, right: node };
  }
  if (t.kind === "branch") {
    const l = attachSibling(t.left, want, node);
    if (l) return { kind: "branch", left: l, right: t.right };
    const r = attachSibling(t.right, want, node);
    if (r) return { kind: "branch", left: t.left, right: r };
  }
  return null;
}

/**
 * Reparent: move the node identified by `sourceSet` (a single leaf's set, or a
 * whole branch's leaf set) to become a new sibling of the node identified by
 * `targetSet`. Drives both leaf drags and branch drags. Returns the new tree,
 * or null for a no-op / impossible drop (onto itself or its own subtree).
 * Always yields a valid binary tree, hence a Kraft-valid depth multiset.
 */
export function reparentNode(
  t: ModelTree,
  sourceSet: number[],
  targetSet: number[],
): ModelTree | null {
  const src = new Set(sourceSet);
  const want = new Set(targetSet.filter((i) => !src.has(i)));
  if (want.size === 0) return null; // target lies inside the dragged node
  const d = detach(t, [...src].sort((a, b) => a - b).join(","));
  if (!d) return null;
  return attachSibling(d.rest, want, d.node);
}

/**
 * Rewrite a `.sl` source so each named spend carries the wanted `@depth(n)`
 * (or none, when its entry is null). Any existing `@weight(...)` on the spend
 * is preserved (it lies dormant while the leaf is depth-pinned and takes effect
 * again if the pins are later removed); any existing `@depth(...)` is replaced.
 *
 * Matches a real declaration only (`spend NAME(`), so `spend` inside a comment
 * is left alone.
 */
export function rewriteDepths(src: string, depthByName: Record<string, number | null>): string {
  const decl = /((?:@(?:depth|weight)\s*\([^)]*\)\s*)*)((?:open\s+)?spend\s+)([A-Za-z_]\w*)(?=\s*\()/g;
  return src.replace(decl, (m, decos: string, kw: string, name: string) => {
    if (!(name in depthByName)) return m;
    const want = depthByName[name];
    const weight = (decos.match(/@weight\s*\([^)]*\)/) ?? [])[0];
    const parts: string[] = [];
    if (want != null) parts.push(`@depth(${want})`);
    if (weight) parts.push(weight);
    return (parts.length ? parts.join(" ") + " " : "") + kw + name;
  });
}
